import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useDemoTour } from '../../store/demoTour';

/**
 * Renders fixed-position pulsing cyan rings over elements with data-highlight="id".
 * Zero layout impact — reads positions via getBoundingClientRect.
 * Mount once at the top level.
 */
export function DemoHighlights() {
  const highlights = useDemoTour(s => s.highlights);
  const [rects, setRects] = useState<Record<string, DOMRect>>({});

  const updateRects = useCallback(() => {
    const next: Record<string, DOMRect> = {};
    for (const id of highlights) {
      const el = document.querySelector(`[data-highlight="${id}"]`);
      if (el) next[id] = el.getBoundingClientRect();
    }
    setRects(next);
  }, [highlights]);

  useEffect(() => {
    if (highlights.length === 0) { setRects({}); return; }
    updateRects();
    const interval = setInterval(updateRects, 250);
    window.addEventListener('resize', updateRects);
    return () => { clearInterval(interval); window.removeEventListener('resize', updateRects); };
  }, [highlights, updateRects]);

  if (highlights.length === 0) return null;

  return createPortal(
    <AnimatePresence>
      {highlights.map(id => {
        const rect = rects[id];
        if (!rect || rect.width === 0) return null;
        return (
          <motion.div
            key={id}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
            }}
            className="fixed rounded-lg pointer-events-none"
            style={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
              zIndex: 9999,
              boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.7), 0 0 20px 4px rgba(239, 68, 68, 0.4), 0 0 40px 8px rgba(239, 68, 68, 0.15)',
            }}
          />
        );
      })}
    </AnimatePresence>,
    document.body
  );
}
